import React from "react";
import { Helmet } from "react-helmet-async";
import PortfolioHeader from "./portfolio/components/PortfolioHeader";
import PortfolioFooter from "./portfolio/components/PortfolioFooter";
import HeroSection from "./portfolio/sections/HeroSection";
import ProductShowcase from "./portfolio/sections/ProductShowcase";
import PricingSection from "./portfolio/sections/PricingSection";
import DesktopAppSection from "./portfolio/sections/DesktopAppSection";
import HowItWorksSection from "./portfolio/sections/HowItWorksSection";
import RolesSection from "./portfolio/sections/RolesSection";
import VideoSection from "./portfolio/sections/VideoSection";
import FaqSection from "./portfolio/sections/FaqSection";
import FinalCtaSection from "./portfolio/sections/FinalCtaSection";

// NOTE: When changing the <Helmet> tags below, also update the matching
// prerendered static file at frontend/index.html. nginx serves that file at /
// to non-JS crawlers (LinkedIn, Slack, GPTBot, etc.) so they see real meta
// tags + JSON-LD instead of the SPA shell.
const PortfolioHome: React.FC = () => {
  return (
    <div className="min-h-dvh touch-manipulation bg-slate-50 text-gray-800 font-sans antialiased relative overflow-x-hidden">
      <Helmet>
        <title>Free Employee Time Tracking — Time Tracker by Synquar Ai Labs</title>
        <meta
          name="description"
          content="Free employee time tracking with role-based dashboards, calendar views, and screenshots. Optional paid AI for summary validation."
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link rel="canonical" href="https://time-tracker.synquarailabs.com/" />
        <meta property="og:title" content="Free Employee Time Tracking — Time Tracker by Synquar Ai Labs" />
        <meta
          property="og:description"
          content="Free employee time tracking with role-based dashboards, calendar, and screenshots. Pay only for optional AI features."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://time-tracker.synquarailabs.com/" />
        <meta property="og:site_name" content="Time Tracker by Synquar Ai Labs" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Free Employee Time Tracking — Time Tracker by Synquar Ai Labs" />
        <meta
          name="twitter:description"
          content="Free employee time tracking with optional AI summary validation and session analysis."
        />
      </Helmet>

      {/* Atmospheric background */}
      <div className="pointer-events-none fixed inset-0 -z-10" aria-hidden>
        <div className="absolute -top-32 left-1/2 h-[42rem] w-[min(90rem,140vw)] -translate-x-1/2 rounded-[100%] bg-gradient-to-b from-primary-100/90 via-slate-50/40 to-transparent blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 translate-x-1/3 translate-y-1/4 rounded-full bg-primary-200/25 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <a
        href="#main"
        className="absolute left-4 top-0 z-[100] -translate-y-full rounded-xl bg-white px-4 py-3 text-sm font-medium text-primary-900 shadow-elevated ring-2 ring-primary-500 transition-transform focus:translate-y-4 focus:outline-none"
      >
        Skip to main content
      </a>

      <PortfolioHeader />

      <main id="main">
        {/* Above-the-fold: render immediately. */}
        <HeroSection />
        <ProductShowcase />
        {/* Below-the-fold: defer off-screen render/layout work. Each wrapper is
            a plain full-width block (required for content-visibility to apply)
            with no margin/padding of its own, so the visual result and section
            anchors are unchanged. */}
        <div className="cv-defer">
          <PricingSection />
        </div>
        <div className="cv-defer">
          <DesktopAppSection />
        </div>
        <div className="cv-defer">
          <HowItWorksSection />
        </div>
        <div className="cv-defer">
          <RolesSection />
        </div>
        <div className="cv-defer">
          <VideoSection />
        </div>
        <div className="cv-defer">
          <FaqSection />
        </div>
        <div className="cv-defer">
          <FinalCtaSection />
        </div>
      </main>

      <PortfolioFooter />
    </div>
  );
};

export default PortfolioHome;
